import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ApiService } from 'src/app/services/api.service';
import { AuthGuard, AuthService, Profile } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { EnumToKeyPipe } from 'src/app/services/wording-enum-pipe';
import {
	MessageDestinataireRequest,
	MessageResponse,
	PostMessageEditRequest,
	PostMessageEditResponse,
} from '../../services/dtos';
import { CsvService } from 'src/app/services/csv.service';

@Component({
	selector: 'app-message-modal',
	templateUrl: './message-modal.component.html',
	styleUrls: ['./message-modal.component.scss'],
	providers: [ConfirmationService, NotificationService, EnumToKeyPipe, TranslatePipe],
})
export class MessageModalComponent implements OnInit, OnChanges {
	@Input() messageId?: string | 'new';
	@Output() closeModal: EventEmitter<void> = new EventEmitter();
	@Output() updateList: EventEmitter<void> = new EventEmitter();

	editForm = this.formBuilder.group({
		titre: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
		corps: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(20000)]],
		dateVisibilite: null,
		dateExpiration: null,
		uniqueDestinataireNumeroCheptel: [null, [Validators.minLength(2), Validators.maxLength(20)]],
		uniqueDestinataireEmail: [null, [Validators.email]],
	});
	fileDestinataires: MessageDestinataireRequest[] = [];

	profile: Profile;
	message: MessageResponse;
	failedDestinataires: MessageDestinataireRequest[] = [];
	newMessage = true;
	showSendSummary = false;

	constructor(
		private formBuilder: FormBuilder,
		private apiService: ApiService,
		private authGuard: AuthGuard,
		private authService: AuthService,
		private csvService: CsvService,
		private notificationService: NotificationService,
	) {}

	ngOnInit() {
		this.authService.loadUserProfile().then((profile: Profile) => {
			this.profile = profile;
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.messageId) return;

		if (changes['messageId']?.currentValue) {
			if (this.messageId !== 'new') {
				this.newMessage = false;
				this.apiService.messages_getOne(this.messageId).subscribe((message) => {
					this.updateEditor(message);
				});
			} else {
				this.newMessage = true;
			}
		}
	}

	get form() {
		return this.editForm.controls;
	}

	getDestinataires() {
		return this.fileDestinataires.length > 0
			? this.fileDestinataires
			: this.editForm.get('uniqueDestinataireNumeroCheptel').valid &&
			  this.editForm.get('uniqueDestinataireEmail').valid
			? [
					{
						numeroCheptel: this.editForm.get('uniqueDestinataireNumeroCheptel').value,
						email: this.editForm.get('uniqueDestinataireEmail').value,
					},
			  ]
			: [];
	}

	resetDestinataires() {
		this.fileDestinataires = [];
		this.editForm.get('uniqueDestinataireEmail').setValue(null);
		this.editForm.get('uniqueDestinataireNumeroCheptel').setValue(null);
	}

	get destinatairesCount() {
		return this.getDestinataires().length;
	}

	get formIsValid() {
		return this.destinatairesCount > 0 && this.editForm.get('titre').valid && this.editForm.get('corps').valid;
	}

	updateEditor(data: MessageResponse) {
		this.message = data;

		this.editForm.get('titre').setValue(data.titre);
		this.editForm.get('corps').setValue(data.corps);
		if (data.dateVisibilite) {
			this.editForm.get('dateVisibilite').setValue(new Date(data.dateVisibilite));
		}
		if (data.dateExpiration) {
			this.editForm.get('dateExpiration').setValue(new Date(data.dateExpiration));
		}

		this.fileDestinataires = data?.destinataires ?? [];
		if (this.fileDestinataires.length == 1) {
			this.editForm.get('uniqueDestinataireEmail').setValue(this.fileDestinataires[0].email);
			this.editForm.get('uniqueDestinataireNumeroCheptel').setValue(this.fileDestinataires[0].numeroCheptel);
			this.fileDestinataires = [];
		}
		this.setVisibilityBadge();
	}

	resetAndClose() {
		this.editForm.reset();
		this.fileDestinataires = [];
		this.failedDestinataires = [];
		this.showSendSummary = false;
		this.messageId = undefined;
		this.closeModal.emit();
	}

	private checkFormat(x: unknown): x is MessageDestinataireRequest {
		if (typeof x['email'] === 'string' && typeof x['numeroCheptel'] === 'string') {
			return true;
		}
		return false;
	}

	async importDataFromCSV(event: Event) {
		const fileContent = await this.getTextFromFile(event);
		try {
			this.fileDestinataires = this.csvService.importDataFromCSV(fileContent, this.checkFormat);
		} catch (e) {
			this.notificationService.notifyError(
				"Erreur d'import. Veuillez vérifier le fichier et le format des données.",
			);
		}
	}

	private async getTextFromFile(event: Event) {
		const input = event.target as HTMLInputElement;
		const file: File = input.files[0];
		const fileContent = await file.text();

		return fileContent;
	}

	downloadTemplateFile = () => {
		const url = '/assets/template/template-destinataires.csv';
		const linkElement = document.createElement('a');
		linkElement.setAttribute('href', url);
		linkElement.click();
	};

	handleSaveResponse(data: PostMessageEditResponse) {
		this.showSendSummary = true;
		this.message = data.message;

		if (data.failedDestinataireRequests.length > 0) {
			this.failedDestinataires = data.failedDestinataireRequests;
		}

		this.updateList.emit();
	}

	dateAtEndOfDay(date: Date) {
		const selectedDay = date.getDate();
		const datePlusOneDay = new Date(date.setDate(selectedDay + 1));
		return new Date(datePlusOneDay.setUTCHours(21, 59, 59, 999));
	}

	onSave() {
		const endDate = this.editForm.value.dateExpiration
			? this.dateAtEndOfDay(new Date(this.editForm.value.dateExpiration))
			: null;
		const destinataires =
			this.fileDestinataires.length > 0
				? this.fileDestinataires
				: [
						{
							numeroCheptel: this.editForm.get('uniqueDestinataireNumeroCheptel').value,
							email: this.editForm.get('uniqueDestinataireEmail').value,
						},
				  ];

		const request: PostMessageEditRequest = {
			titre: this.editForm.value.titre,
			corps: this.editForm.value.corps,
			dateVisibilite: this.editForm.value.dateVisibilite,
			dateExpiration: endDate?.toISOString(),
			destinataires: destinataires,
		};

		if (this.messageId !== 'new') {
			this.apiService.messages_update(this.messageId, request).subscribe(
				(data) => {
					this.handleSaveResponse(data);
					this.notificationService.notifySuccess('Message enregistré.');
				},
				(err) => {
					this.notificationService.notifyError(
						`Erreur d'envoi du message. Merci de vérifier les données envoyées.` + err.statusText,
					);
				},
			);
		} else {
			this.apiService.messages_new(request).subscribe((data) => {
				this.handleSaveResponse(data);
			});
		}
	}

	downloadDestinataires() {
		const basicDestinatairesList = this.fileDestinataires.map((d) => ({
			numeroCheptel: d.numeroCheptel,
			email: d.email,
		}));
		const csvContent = this.csvService.saveDataInCSV(basicDestinatairesList);

		const a = document.createElement('a');
		a.href = 'data:text/csv;charset=utf-8,' + encodeURI(csvContent);
		a.target = '_blank';
		a.download = `destinataires_${this.messageId}.csv`;
		a.click();
		a.remove();
	}

	isCurrentlyVisible = false;

	setVisibilityBadge() {
		console.log('setVisibilityBadge');
		const from = this.editForm.value.dateVisibilite;
		const to = this.editForm.value.dateExpiration
			? this.dateAtEndOfDay(new Date(this.editForm.value.dateExpiration))
			: null;

		this.isCurrentlyVisible = false;

		if (from && to) {
			const now = new Date();
			this.isCurrentlyVisible = new Date(from).getTime() <= now.getTime() && now.getTime() < to.getTime();
		}
	}
}
